import { createResource, createSignal, For, Show } from "solid-js";

import { fetchMyToken, fetchStats } from "../api";

type Endpoint = {
  method: "GET";
  path: string;
  title: string;
  desc: string;
  sample: string;
  curl: string;
};

const ENDPOINTS: Endpoint[] = [
  {
    method: "GET",
    path: "/api/firms",
    title: "List firms",
    desc: "All 500 firms, summary fields only (rank, sector, ticker, revenue, market cap, branding).",
    sample: `{
  "count": 500,
  "items": [
    {
      "slug": "walmart",
      "name": "Walmart",
      "ticker": "WMT",
      "f500_rank": 2,
      "revenue_b_usd_fy2025": 713.16,
      "sector": "Consumer Staples",
      "industry": "Consumer Staples Distribution & Retail",
      "hq": "Bentonville, AR",
      "employees": 2100000,
      "pitch": "World's largest retailer; mass merch plus grocery; ...",
      "modeling_status": "queued",
      "priority_order": 1,
      "market_cap": 1047786703196.75,
      "primary_exchange": "XNAS",
      "branding": { "icon_path": "...", "logo_path": "..." }
    }
  ]
}`,
    curl: `curl -s {BASE}/api/firms | jq '.items[0]'`,
  },
  {
    method: "GET",
    path: "/api/firms/{slug}",
    title: "Get one firm (full record)",
    desc: "Everything: framework axes, financials, operations, AI posture, ticker metadata, branding paths.",
    sample: `{
  "slug": "walmart",
  "name": "Walmart",
  "framework": {
    "clearing_layer": "...",
    "verification": "...",
    "surviving_moats": "...",
    "compression_risk": "...",
    "fork": "...",
    "verdict": "..."
  },
  "financials": { "annual": { "income": { "revenue": 713163000000, ... } } },
  "operations": { "scale": [...], "rhythm": [...] },
  "ai_posture": { "what_survives": "...", "what_compresses": "..." }
}`,
    curl: `curl -s {BASE}/api/firms/walmart`,
  },
  {
    method: "GET",
    path: "/api/firms/{slug}/peers?limit=6",
    title: "Same-sector peers",
    desc: "Anchor firm + up to (limit-1) GICS peers, sorted by revenue desc. Includes sector medians for revenue, operating margin, free cash flow, market cap.",
    sample: `{
  "sector": "Consumer Staples",
  "items": [
    { "slug": "walmart", "is_anchor": true, "revenue": 713163000000, "operating_margin": 0.041, ... }
  ],
  "median": { "revenue": ..., "operating_margin": ..., "free_cash_flow": ..., "market_cap": ... }
}`,
    curl: `curl -s "{BASE}/api/firms/walmart/peers?limit=6"`,
  },
  {
    method: "GET",
    path: "/api/firms/{slug}/branding/{kind}",
    title: "Branding asset",
    desc: "Streams the firm's icon or logo file. `kind` is `icon` or `logo`. Used by the SPA to render firm avatars.",
    sample: "<binary image>",
    curl: `curl -sO {BASE}/api/firms/walmart/branding/logo`,
  },
  {
    method: "GET",
    path: "/api/sectors",
    title: "List sectors",
    desc: "Distinct GICS sector names (12 sectors).",
    sample: `{ "items": ["Communication Services", "Consumer Discretionary", "Consumer Staples", ...] }`,
    curl: `curl -s {BASE}/api/sectors`,
  },
  {
    method: "GET",
    path: "/api/sectors/summary",
    title: "Sectors with aggregates",
    desc: "Per-sector firm count, combined revenue, combined market cap, modeling progress (modeled / queued / in_progress), top 8 firms.",
    sample: `{
  "items": [
    {
      "name": "Information Technology",
      "firm_count": 51,
      "combined_revenue_b_usd": 2841.5,
      "combined_market_cap_b_usd": 23104.2,
      "modeled": 1, "queued": 50, "in_progress": 0,
      "top_firms": [{ "slug": "apple", "ticker": "AAPL", "name": "Apple" }, ...]
    }
  ]
}`,
    curl: `curl -s {BASE}/api/sectors/summary`,
  },
  {
    method: "GET",
    path: "/api/heatmap",
    title: "Sector to firm treemap",
    desc: "GICS sector hierarchy for treemap rendering. Size = market cap (USD billions), color = operating margin.",
    sample: `{
  "items": [
    {
      "name": "Information Technology",
      "market_cap_b_usd": 23104.2,
      "revenue_b_usd": 2841.5,
      "firms": [{ "slug": "apple", "market_cap_b_usd": 3850.1, "operating_margin": 0.31, ... }]
    }
  ]
}`,
    curl: `curl -s {BASE}/api/heatmap`,
  },
  {
    method: "GET",
    path: "/api/stats",
    title: "Registry stats",
    desc: "Total firms, status counts, distinct sectors, combined revenue, latest financial-pull timestamp.",
    sample: `{
  "total": 500,
  "by_status": { "queued": 499, "in_progress": 0, "done": 1 },
  "sectors": 12,
  "combined_revenue_b_usd": 20980.4,
  "firms_with_financials": 467,
  "latest_pulled_at": "2026-06-11T10:29:12+00:00"
}`,
    curl: `curl -s {BASE}/api/stats`,
  },
  {
    method: "GET",
    path: "/healthz",
    title: "Health check",
    desc: "Liveness probe.",
    sample: `{ "ok": true }`,
    curl: `curl -s {BASE}/healthz`,
  },
];

function origin(): string {
  if (typeof window !== "undefined") return window.location.origin;
  return "https://field-study.example";
}

function renderCurl(template: string, token?: string | null): string {
  const withBase = template.replaceAll("{BASE}", origin());
  // /healthz is public; everything else needs the access token. Inject the
  // header right after the curl flags so the copied command works as-is. When
  // the buyer is signed in we splice in their real token; otherwise we leave the
  // $F500_TOKEN env-var placeholder so the public page ships no live credential.
  if (withBase.includes("/healthz")) return withBase;
  const cred = token ?? "$F500_TOKEN";
  return withBase.replace(
    /^curl (-\S+) /,
    `curl $1 -H "Authorization: Bearer ${cred}" `,
  );
}

function agentBlock(token?: string | null): string {
  const base = origin();
  const tok = token ?? "YOUR_TOKEN";
  // Signed-in buyers get their live token spliced in so the whole block is
  // paste-and-go for an agent. Anon visitors keep the YOUR_TOKEN placeholder
  // plus a hint to sign in, and the public bundle never carries a real token.
  const authNote = token
    ? `Auth: every /api/* endpoint requires your access token, sent as a header:
Authorization: Bearer <token>. Your token is filled in below, so these calls work
as-is. The token is yours; do not share it. Calls without it return 401. Example:
  curl -s -H "Authorization: Bearer ${tok}" ${base}/api/stats`
    : `Auth: every /api/* endpoint requires your access token (the code on your purchase
receipt). Send it as a header: Authorization: Bearer <token>. Calls without a valid
token return 401. The token is yours; do not share it. Sign in on the portal and this
block fills in your token automatically. Example:
  curl -s -H "Authorization: Bearer ${tok}" ${base}/api/stats`;
  return `# Fortune 500 Business Models API

Read-only JSON over HTTP. The portal exposes the Fortune 500 2026 registry: 500 firms,
12 GICS sectors, framework axes (clearing layer / verification / surviving moats /
compression risk / fork / verdict), audited financials, and operations metadata.

Base URL: ${base}
OpenAPI:  ${base}/openapi.json
Swagger:  ${base}/docs

Identifiers are kebab-case slugs (e.g. "walmart", "goldman-sachs", "delta-air-lines").

${authNote}

Endpoints:
  GET ${base}/api/stats                          ← registry counts, last refresh
  GET ${base}/api/sectors                        ← sector names
  GET ${base}/api/sectors/summary                ← per-sector aggregates + top 8
  GET ${base}/api/heatmap                        ← sector treemap (market cap, margin)
  GET ${base}/api/firms                          ← all 500 firms, summary fields
  GET ${base}/api/firms/{slug}                   ← one firm, full record
  GET ${base}/api/firms/{slug}/peers?limit=6     ← anchor + GICS peers + medians
  GET ${base}/api/firms/{slug}/branding/{kind}   ← icon or logo (binary)

Units: financials are absolute USD (e.g. 713163000000 for $713.16B). The
revenue_b_usd_fy2025 field on /api/firms is already in USD billions.
All responses are JSON except /branding/{kind} which is binary.
`;
}

export default function Api() {
  const [statsRes] = createResource(fetchStats);
  const [tokenRes] = createResource(fetchMyToken);
  const [copied, setCopied] = createSignal<string | null>(null);

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1200);
    } catch {
      setCopied("error");
      setTimeout(() => setCopied((k) => (k === "error" ? null : k)), 1200);
    }
  };

  return (
    <section class="api-page">
      <div class="api-head">
        <h2>API</h2>
        <span class="sub">
          REST · JSON · 9 endpoints · base{" "}
          <code class="api-base">{origin()}</code>
        </span>
      </div>

      <Show when={statsRes()}>
        {(s) => (
          <div class="api-stats">
            <span>
              <strong>{s().total}</strong> firms
            </span>
            <span>
              <strong>{s().sectors}</strong> sectors
            </span>
            <span>
              <strong>{s().firms_with_financials}</strong> with financials
            </span>
            <span>
              last refresh{" "}
              <strong>
                {s().latest_pulled_at ? s().latest_pulled_at!.slice(0, 10) : "n/a"}
              </strong>
            </span>
          </div>
        )}
      </Show>

      <div class="api-row">
        <div class="api-card api-agent">
          <div class="api-card-head">
            <h3>For agents and LLMs</h3>
            <button
              type="button"
              class="api-copy"
              onClick={() => copy("agent", agentBlock(tokenRes()))}
              title="Copy the block below to paste into a prompt"
            >
              {copied() === "agent" ? "copied" : "copy block"}
            </button>
          </div>
          <p class="api-card-desc">
            Paste this into a chat/IDE so the model can call the API directly. Includes base URL,
            slugs, unit conventions, and the full endpoint list.
          </p>
          <Show
            when={tokenRes()}
            fallback={
              <p class="api-token-note dim">
                <a href="/login">Sign in</a> to fill this block (and the curl examples below) with
                your access token, ready to hand to an agent.
              </p>
            }
          >
            <p class="api-token-note">
              Your access token is filled in below. Treat it like a password: anyone with this block
              can call the API as you.
            </p>
          </Show>
          <pre class="api-pre"><code>{agentBlock(tokenRes())}</code></pre>
        </div>

        <div class="api-card api-schema">
          <div class="api-card-head">
            <h3>Machine-readable</h3>
          </div>
          <p class="api-card-desc">
            FastAPI auto-generates an OpenAPI 3.1 spec and an interactive Swagger UI. The spec and
            docs are public; the data endpoints they describe need your access token (use the
            Swagger "Authorize" button, or send <code>Authorization: Bearer &lt;token&gt;</code>).
          </p>
          <ul class="api-links">
            <li>
              <a href="/openapi.json" target="_blank" rel="noreferrer">
                /openapi.json
              </a>
              <span class="dim">— OpenAPI 3.1 spec, JSON</span>
            </li>
            <li>
              <a href="/docs" target="_blank" rel="noreferrer">
                /docs
              </a>
              <span class="dim">— Swagger UI, interactive try-it</span>
            </li>
            <li>
              <a href="/redoc" target="_blank" rel="noreferrer">
                /redoc
              </a>
              <span class="dim">— ReDoc, prose-style reference</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="api-endpoints">
        <h3 class="api-section-h">Endpoints</h3>
        <For each={ENDPOINTS}>
          {(ep) => {
            const curl = () => renderCurl(ep.curl, tokenRes());
            const key = ep.path + ep.title;
            return (
              <article class="api-ep">
                <div class="api-ep-head">
                  <span class="api-method">{ep.method}</span>
                  <code class="api-path">{ep.path}</code>
                  <span class="api-ep-title">{ep.title}</span>
                </div>
                <p class="api-ep-desc">{ep.desc}</p>
                <div class="api-ep-curl">
                  <pre class="api-pre"><code>{curl()}</code></pre>
                  <button
                    type="button"
                    class="api-copy api-copy-sm"
                    onClick={() => copy(key + ":curl", curl())}
                    title="Copy curl command"
                  >
                    {copied() === key + ":curl" ? "copied" : "copy"}
                  </button>
                </div>
                <details class="api-ep-sample">
                  <summary>Sample response</summary>
                  <pre class="api-pre"><code>{ep.sample}</code></pre>
                </details>
              </article>
            );
          }}
        </For>
      </div>

      <div class="api-card api-conv">
        <h3>Conventions</h3>
        <ul class="api-conv-list">
          <li>
            <span class="k">Identifiers</span>
            <span class="v">
              kebab-case slugs (e.g. <code>goldman-sachs</code>, <code>delta-air-lines</code>,
              <code>unitedhealth</code>).
            </span>
          </li>
          <li>
            <span class="k">Units</span>
            <span class="v">
              financials are absolute USD; <code>revenue_b_usd_fy2025</code> is already in USD
              billions; <code>market_cap</code> is absolute USD.
            </span>
          </li>
          <li>
            <span class="k">Status values</span>
            <span class="v">
              <code>modeling_status</code> is one of <code>queued</code>, <code>in_progress</code>,
              <code>done</code>. Only <code>done</code> firms have a <code>dashboard_path</code>.
            </span>
          </li>
          <li>
            <span class="k">Errors</span>
            <span class="v">
              <code>404</code> for unknown slug or missing branding asset; <code>422</code> for
              malformed query params; all other paths return <code>200</code> with JSON.
            </span>
          </li>
          <li>
            <span class="k">Auth</span>
            <span class="v">
              required on every <code>/api/*</code> endpoint. Send your access token (the code on
              your receipt) as <code>Authorization: Bearer &lt;token&gt;</code>. In the browser a
              signed-in session cookie works too. Missing or invalid credentials return{" "}
              <code>401</code>. <code>/healthz</code> is the only open endpoint.
            </span>
          </li>
          <li>
            <span class="k">CORS</span>
            <span class="v">
              <code>GET</code> only, from the same origin. Use a server-side proxy for cross-origin
              browser apps.
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
}
